import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Popover, Tooltip } from '@ets/design-system';
import { useSelector, useDispatch } from '_common/hooks';
import { openAndUpdateModal } from '_common/modals/ModalsSlice';
import EditorManager from 'Editor/services/EditorManager';
import ColorsMenu from 'Editor/components/ColorsMenu/ColorsMenu';
import { SplitToggle } from '_common/components';
import { CustomIcon } from '_common/lib/components';
import { EditorDOMUtils } from 'Editor/services/_Common/DOM';
import { ReduxInterface } from 'Editor/services';
import { getFormattedColor } from 'assets/colors';

const ParagraphBackgroundColor = ({ isReadOnlyMode }: { isReadOnlyMode: boolean }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const paragraphBackgroundColor = useSelector((state) => {
    const paragraphColor = state.editor.toolbar.paragraphColor;

    if (paragraphColor) {
      if (typeof paragraphColor === 'boolean') {
        return !paragraphColor ? 'transparent' : '';
      } else {
        return paragraphColor.replace(/ /g, '');
      }
    }

    return '';
  });

  const [documentColors, setDocumentColors] = useState<
    { r: string | number; g: string | number; b: string | number; rgb: string }[]
  >([]);
  const [isOpen, setIsOpen] = useState(false);

  const onOpenColorsPopover = () => {
    const documentColors = EditorDOMUtils.getDocumentColors();
    if (documentColors) {
      setDocumentColors(documentColors);
    }
  };

  const applyColor = (color: string) => {
    EditorManager.getInstance().applyParagraphBackgroundColor(color);
  };

  const onParagraphBackgroundColorChange = (color: string) => {
    ReduxInterface.updateToolbarValues({
      paragraphColor: color,
    });
    applyColor(color);
    setIsOpen(false);
  };

  const onClearParagraphBackgroundColor = () => {
    ReduxInterface.updateToolbarValues({
      paragraphColor: 'transparent',
    });
    EditorManager.getInstance().applyParagraphBackgroundColor('transparent');
  };

  const openColorPickerModal = () => {
    dispatch(
      openAndUpdateModal({
        modal: 'ColorPickerModal',
        data: {
          action: 'applyParagraphBackgroundColor',
        },
      }),
    );
  };

  return (
    <Popover.Root placement="bottom-start" open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger>
        <Tooltip
          content={intl.formatMessage({ id: 'PARAGRAPH_BACKGROUND' })}
          testId="paragraph-background-color"
        >
          <div>
            <SplitToggle
              isToggled={false}
              isOpen={isOpen}
              disabled={isReadOnlyMode}
              testId="paragraph-background-color"
              onClick={() => {
                applyColor(paragraphBackgroundColor);
                setIsOpen(false);
              }}
              customIcon={
                <CustomIcon
                  name="BackgroundColor"
                  color={
                    paragraphBackgroundColor
                      ? getFormattedColor(paragraphBackgroundColor)
                      : '#6D6D82'
                  }
                  size={24}
                />
              }
              onPopperClick={() => {
                onOpenColorsPopover();
                setIsOpen(!isOpen);
              }}
              margin="2px"
            />
          </div>
        </Tooltip>
      </Popover.Trigger>
      <Popover.Content testId="paragraph-background-color">
        <ColorsMenu
          documentColors={documentColors}
          toggle={() => setIsOpen(false)}
          changeBackgroundColor={(color) => {
            onParagraphBackgroundColorChange(color);
            setIsOpen(false);
          }}
          clearBackgroundColor={() => {
            onClearParagraphBackgroundColor();
            setIsOpen(false);
          }}
          selected={paragraphBackgroundColor}
          setOpenColorPickerModal={openColorPickerModal}
        />
      </Popover.Content>
    </Popover.Root>
  );
};

export default ParagraphBackgroundColor;
